/**
 * Quiz Run Sync Service
 *
 * Syncs detailed quiz run data to Supabase for logged-in users.
 * Stores all question attempts, answers, and timing data for detailed analytics.
 *
 * Only syncs when user is authenticated - gracefully degrades for offline/anonymous users.
 */

import { supabase, isSupabaseConfigured, getCurrentUser } from './supabase';
import { db } from '@/db/database';

export interface QuizRunAttemptData {
  answer: string;
  timestamp: string;
}

export interface QuizRunAttempt {
  question_number: number;
  feature_id: string;
  feature_label: string;
  prompt_text: string;
  prompt_keys: string[];
  correct_answer: string;
  user_attempts: QuizRunAttemptData[];
  final_result: 'correct' | 'incorrect';
  attempts_used: number;
  time_spent_ms: number;
  category: string;
  region: string;
  granularity_level: number;
  attempted_at: string;
}

export interface QuizRunData {
  quiz_signature: string;
  quiz_title: string;
  local_session_id: string;
  started_at: string;
  completed_at: string;
  total_questions: number;
  correct_answers: number;
  incorrect_answers: number;
  accuracy_rate: number;
  total_time_ms: number;
  average_time_per_question_ms: number;
  difficulty_multiplier?: number;
  display_settings?: any;
  attempts: QuizRunAttempt[];
}

/**
 * Sync quiz run data to Supabase
 *
 * @param sessionId - Local IndexedDB session ID
 * @returns Success status
 */
export async function syncQuizRunToSupabase(sessionId: string): Promise<boolean> {
  try {
    // Check if Supabase is configured
    if (!isSupabaseConfigured) {
      console.log('[QuizSync] Supabase not configured, skipping sync');
      return false;
    }

    // Check if user is authenticated
    const user = await getCurrentUser();
    if (!user) {
      console.log('[QuizSync] User not authenticated, skipping sync');
      return false;
    }

    console.log('[QuizSync] Starting sync for session:', sessionId);

    // Get session data from IndexedDB
    const session = await db.quiz_sessions.get(sessionId);
    if (!session) {
      console.error('[QuizSync] Session not found:', sessionId);
      return false;
    }

    // Get quiz config
    const config = await db.quiz_configs.get(session.quizSignature);
    if (!config) {
      console.error('[QuizSync] Quiz config not found:', session.quizSignature);
      return false;
    }

    // Get all attempts for this session
    const attempts = await db.quiz_attempts
      .where('sessionId')
      .equals(sessionId)
      .toArray();

    if (attempts.length === 0) {
      console.warn('[QuizSync] No attempts found for session:', sessionId);
      return false;
    }

    // Group attempts by feature (in case of multiple attempts per question)
    const attemptsByFeature = new Map<string, typeof attempts>();
    attempts.forEach((attempt) => {
      const key = attempt.featureId;
      if (!attemptsByFeature.has(key)) {
        attemptsByFeature.set(key, []);
      }
      attemptsByFeature.get(key)!.push(attempt);
    });

    // Build quiz run attempts data
    const runAttempts: QuizRunAttempt[] = [];
    let questionNumber = 1;

    // Sort attempts by attempted_at to maintain question order
    const sortedAttempts = [...attemptsByFeature.values()].sort((a, b) => {
      const aTime = new Date(a[0]!.attemptedAt).getTime();
      const bTime = new Date(b[0]!.attemptedAt).getTime();
      return aTime - bTime;
    });

    for (const featureAttempts of sortedAttempts) {
      const firstAttempt = featureAttempts[0]!;

      // Build user attempts array
      // For multi-attempt questions, we only track the final answer currently
      const userAttempts: QuizRunAttemptData[] = [];

      if (firstAttempt.clickedFeatureLabel) {
        // We have the actual clicked feature
        userAttempts.push({
          answer: firstAttempt.clickedFeatureLabel,
          timestamp: firstAttempt.attemptedAt,
        });
      } else if (firstAttempt.wasCorrect) {
        // No clicked label, but it was correct
        userAttempts.push({
          answer: firstAttempt.featureLabel,
          timestamp: firstAttempt.attemptedAt,
        });
      }

      // If multiple attempts were used but we only have one record,
      // add placeholders for the earlier attempts
      for (let i = 1; i < firstAttempt.attemptsUsed; i++) {
        userAttempts.unshift({
          answer: '[Earlier attempt not tracked]',
          timestamp: firstAttempt.attemptedAt,
        });
      }

      runAttempts.push({
        question_number: questionNumber++,
        feature_id: firstAttempt.featureId,
        feature_label: firstAttempt.featureLabel,
        prompt_text: firstAttempt.promptText,
        prompt_keys: firstAttempt.promptKeys,
        correct_answer: firstAttempt.featureLabel, // The correct answer IS the feature label
        user_attempts: userAttempts,
        final_result: firstAttempt.wasCorrect ? 'correct' : 'incorrect',
        attempts_used: firstAttempt.attemptsUsed,
        time_spent_ms: firstAttempt.timeSpentMs,
        category: firstAttempt.category,
        region: firstAttempt.region,
        granularity_level: firstAttempt.granularityLevel,
        attempted_at: firstAttempt.attemptedAt,
      });
    }

    // Calculate started_at (first attempt's timestamp)
    const startedAt = attempts.length > 0
      ? attempts.reduce((earliest, a) => {
          const t = new Date(a.attemptedAt).getTime();
          return t < new Date(earliest).getTime() ? a.attemptedAt : earliest;
        }, attempts[0]!.attemptedAt)
      : session.completedAt;

    // Build quiz run data
    const runData: QuizRunData = {
      quiz_signature: session.quizSignature,
      quiz_title: config.title,
      local_session_id: session.id,
      started_at: startedAt,
      completed_at: session.completedAt,
      total_questions: session.totalQuestions,
      correct_answers: session.correctAnswers,
      incorrect_answers: session.incorrectAnswers,
      accuracy_rate: session.accuracyRate,
      total_time_ms: session.totalTimeMs,
      average_time_per_question_ms: session.averageTimePerQuestionMs,
      difficulty_multiplier: session.difficultyMultiplier,
      display_settings: session.displaySettings,
      attempts: runAttempts,
    };

    // Insert quiz run
    const { data: quizRun, error: runError } = await supabase
      .from('quiz_runs')
      .insert({
        user_id: user.id,
        quiz_signature: runData.quiz_signature,
        quiz_title: runData.quiz_title,
        local_session_id: runData.local_session_id,
        started_at: runData.started_at,
        completed_at: runData.completed_at,
        total_questions: runData.total_questions,
        correct_answers: runData.correct_answers,
        incorrect_answers: runData.incorrect_answers,
        accuracy_rate: runData.accuracy_rate,
        total_time_ms: runData.total_time_ms,
        average_time_per_question_ms: runData.average_time_per_question_ms,
        difficulty_multiplier: runData.difficulty_multiplier || 1.0,
        display_settings: runData.display_settings || null,
      } as any)
      .select()
      .single();

    if (runError) {
      console.error('[QuizSync] Error inserting quiz run:', runError);
      return false;
    }

    if (!quizRun) {
      console.error('[QuizSync] No quiz run returned after insert');
      return false;
    }

    const runId = (quizRun as any).id;
    console.log('[QuizSync] Quiz run created:', runId);

    // Insert attempts
    const attemptInserts = runData.attempts.map((attempt) => ({
      run_id: runId,
      question_number: attempt.question_number,
      feature_id: attempt.feature_id,
      feature_label: attempt.feature_label,
      prompt_text: attempt.prompt_text,
      prompt_keys: attempt.prompt_keys,
      correct_answer: attempt.correct_answer,
      user_attempts: attempt.user_attempts,
      final_result: attempt.final_result,
      attempts_used: attempt.attempts_used,
      time_spent_ms: attempt.time_spent_ms,
      category: attempt.category,
      region: attempt.region,
      granularity_level: attempt.granularity_level,
      attempted_at: attempt.attempted_at,
    }));

    const { error: attemptsError } = await supabase
      .from('quiz_run_attempts')
      .insert(attemptInserts as any);

    if (attemptsError) {
      console.error('[QuizSync] Error inserting attempts:', attemptsError);
      // Try to clean up the run
      await supabase.from('quiz_runs').delete().eq('id', runId);
      return false;
    }

    console.log('[QuizSync] Successfully synced', runAttempts.length, 'attempts');
    return true;
  } catch (error) {
    console.error('[QuizSync] Unexpected error during sync:', error);
    return false;
  }
}

/**
 * Fetch detailed quiz run data from Supabase
 *
 * @param localSessionId - Local IndexedDB session ID
 * @returns Quiz run data or null if not found
 */
export async function fetchQuizRunFromSupabase(
  localSessionId: string
): Promise<QuizRunData | null> {
  try {
    if (!isSupabaseConfigured) {
      return null;
    }

    const user = await getCurrentUser();
    if (!user) {
      return null;
    }

    // Use the helper function from migration
    const { data, error } = await supabase.rpc('get_quiz_run_details', {
      p_local_session_id: localSessionId,
    } as any);

    if (error) {
      console.error('[QuizSync] Error fetching quiz run:', error);
      return null;
    }

    if (!data || (data as any).length === 0) {
      console.log('[QuizSync] No quiz run found for session:', localSessionId);
      return null;
    }

    const run = (data as any)[0];

    return {
      quiz_signature: run.quiz_signature,
      quiz_title: run.quiz_title,
      local_session_id: localSessionId,
      started_at: run.completed_at, // Placeholder, would need to add started_at to function
      completed_at: run.completed_at,
      total_questions: run.total_questions,
      correct_answers: run.correct_answers,
      incorrect_answers: run.total_questions - run.correct_answers,
      accuracy_rate: run.accuracy_rate,
      total_time_ms: run.total_time_ms,
      average_time_per_question_ms: Math.round(run.total_time_ms / run.total_questions),
      difficulty_multiplier: run.difficulty_multiplier,
      display_settings: run.display_settings,
      attempts: run.attempts || [],
    };
  } catch (error) {
    console.error('[QuizSync] Unexpected error fetching quiz run:', error);
    return null;
  }
}
